import {
  getPaginationRowModel,
  getFilteredRowModel,
  ColumnFiltersState,
  getSortedRowModel,
  getCoreRowModel,
  PaginationState,
  useReactTable,
  SortingState,
} from '@ui/components';
import {
  OpensearchSearchRequestOpenSearchDataTypeEnum,
  OpensearchSearchResponseListObject,
} from '@interfaces/models';
import { getOpensearchTableConfig, getOpensearchTableData } from '@api/opensearch';
import { useInfiniteQuery, useQueryClient, useQuery } from '@tanstack/react-query';
import { getTableFilters, getTableSort } from '@shared/utils';
import { useCallback, useState, useMemo } from 'react';
import { useAutoColumns } from '@shared/components';
import { useDebounce } from '@ui/hooks';

import { InvoicingActionColumn } from './invoicing-action-column';

const initialPageParam = { pageSize: 50, scrollId: undefined };

export const useInvoicingTable = ({ pageSize = 50 }: { pageSize?: number }) => {
  const queryClient = useQueryClient();

  const [tableSearchQuery, setTableSearchQuery] = useState('');
  const debouncedTableSearchQuery = useDebounce(tableSearchQuery, 800);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [rowSelection, setRowSelection] = useState({});
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: pageSize,
  });
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'INVOICING_INVOICE_SALE_DATE', desc: true },
  ]);

  const { data: tableConfig, isLoading: isTableConfigLoading } = useQuery({
    queryKey: ['invoicing-table-config'],
    queryFn: () => getOpensearchTableConfig(OpensearchSearchRequestOpenSearchDataTypeEnum.INVOICES),
  });

  const { columnDefinitions } = useAutoColumns({
    columns: tableConfig ?? [],
    actions: InvoicingActionColumn(),
    maxColumnWidth: 150,
  });

  const queryParams = useMemo(
    () => ({
      sort: getTableSort(sorting),
      filters: getTableFilters(2, debouncedTableSearchQuery),
    }),
    [debouncedTableSearchQuery, sorting],
  );

  const {
    data: infiniteData,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading,
    refetch: refetchTable,
  } = useInfiniteQuery({
    queryFn: ({ pageParam, signal }) => {
      return getOpensearchTableData(
        {
          openSearchDataType: OpensearchSearchRequestOpenSearchDataTypeEnum.INVOICES,
          ...queryParams,
          ...pageParam,
        },
        signal,
      );
    },
    getNextPageParam: (lastPage: OpensearchSearchResponseListObject) => {
      return {
        pageSize: pageSize,
        scrollId: lastPage.scrollId || undefined,
      };
    },
    queryKey: ['invoicing-list', queryParams],
    initialPageParam: {
      pageSize: pageSize ?? initialPageParam.pageSize,
      scrollId: initialPageParam.scrollId,
    },
  });

  const data = useMemo(
    () => infiniteData?.pages.flatMap((page) => page.data) ?? [],
    [infiniteData],
  );

  const totalItems = useMemo(() => infiniteData?.pages[0]?.totalHits ?? 0, [infiniteData]);

  const loadedItems = useMemo(() => {
    return data.length;
  }, [data]);

  const table = useReactTable({
    data: data ?? [],
    columns: columnDefinitions,
    state: { pagination, sorting, globalFilter: tableSearchQuery, columnFilters, rowSelection },
    pageCount: Math.ceil(totalItems / pagination.pageSize),
    rowCount: totalItems,
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    getRowId: (row) => row[0] as string,
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    onGlobalFilterChange: setTableSearchQuery,
    onColumnFiltersChange: setColumnFilters,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
  });

  const resetTable = useCallback(() => {
    queryClient.resetQueries({ queryKey: ['invoicing-list'] });
  }, [queryClient]);

  return {
    globalFilter: table.getState().globalFilter,
    columnsLength: columnDefinitions.length,
    isTableConfigLoading,
    fetchNextPage,
    refetchTable,
    hasNextPage,
    loadedItems,
    resetTable,
    totalItems,
    isFetching,
    isLoading,
    table,
  };
};
